import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faMousePointer, faPlus, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import React from 'react';
import type { FormatOptionLabelMeta } from 'react-select-modern';

import type { Campaign } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import { Pill } from '@feathr/components';
import { fieldDataTypeIcon } from '@feathr/extender/styles/fieldDataType';

import { getCampaignIcon } from '../CampaignIcons';
import type { IFieldOptionData } from './SelectOptions.utils';
import { valueToFieldDataType } from './SelectOptions.utils';

import * as styles from './SelectOptions.css';

/*
 * formatOptionLabel renderers for the UniversalSelect family (react-select v5).
 *
 * These replace the legacy pattern of overriding `components.Option` and `components.SingleValue`
 * with a pair of wrapper components. react-select v5 calls formatOptionLabel for both the menu rows
 * and the selected value, passing `context` to distinguish them, so one function covers what
 * previously took two components — and nothing here has to wrap a version-specific react-select
 * internal, which is what made the v3 option components unusable inside a v5 select.
 */

const collectionIcons: Partial<Record<FieldCollection, IconDefinition>> = {
  [FieldCollection.Breadcrumb]: faMousePointer,
  [FieldCollection.Person]: faUser,
};

function getFieldIcon(field: IFieldOptionData): IconDefinition {
  if (field.id === 'create') {
    return faPlus;
  }
  return fieldDataTypeIcon(field.data_type ? field.data_type : valueToFieldDataType(field.value));
}

/**
 * Renders a campaign option as its class icon plus its name. The menu row and the selected value
 * render identically, so `context` is not consulted.
 */
export function formatCampaignOptionLabel(campaign: Campaign): ReactNode {
  const attributes = campaign.toJS();

  return (
    <div className={styles.fieldOption}>
      <div className={styles.contentWrapper}>{getCampaignIcon(attributes._cls)}</div>
      <div className={styles.contentWrapper}>
        <span>{attributes.name}</span>
      </div>
    </div>
  );
}

/**
 * Renders a field option as its data-type icon plus its label.
 *
 * The selected value prefers `key`, because a saved mapping identifies its field by key while the
 * options loaded from the API carry `name` / `label` / `u_key`. That asymmetry is why the legacy
 * code needed a separate SingleValue component.
 */
export function formatFieldOptionLabel(
  field: IFieldOptionData,
  meta: FormatOptionLabelMeta<IFieldOptionData>,
): ReactNode {
  const icon = getFieldIcon(field);
  const label =
    meta.context === 'value'
      ? field.key || field.name || field.label || field.u_key
      : field.name || field.label || field.u_key;

  return (
    <div className={styles.fieldOption}>
      <div className={styles.contentWrapper}>{icon && <FontAwesomeIcon icon={icon} />}</div>
      <div className={styles.fieldOptionCell}>
        <span>{label}</span>
      </div>
    </div>
  );
}

/**
 * Renders a field option with a chip naming the collection it belongs to. Used when a select draws
 * fields from more than one collection, so the rows need disambiguating.
 *
 * The chip is menu-only: once selected, the collection is implied by the field itself, and the
 * legacy SingleValue never showed it.
 */
export function formatCollectionFieldOptionLabel(
  field: IFieldOptionData,
  meta: FormatOptionLabelMeta<IFieldOptionData>,
  t: TFunction,
): ReactNode {
  const collectionLabels: Partial<Record<FieldCollection, string>> = {
    [FieldCollection.Breadcrumb]: t('Activity'),
    [FieldCollection.Person]: t('Attribute'),
  };

  const collectionLabel = field.collection ? collectionLabels[field.collection] : undefined;
  const collectionIcon = field.collection ? collectionIcons[field.collection] : undefined;
  const showChip = meta.context === 'menu' && !!collectionLabel;

  if (!showChip) {
    return formatFieldOptionLabel(field, meta);
  }

  return (
    <div className={styles.collectionFieldOption}>
      {formatFieldOptionLabel(field, meta)}
      <div className={styles.fieldOptionCell}>
        <Pill prefix={collectionIcon && <FontAwesomeIcon icon={collectionIcon} />}>
          {collectionLabel}
        </Pill>
      </div>
    </div>
  );
}
